import { EPriceTypes } from '@r1-frontend/shared/types/EPriceTypes';

import { sortFunction } from '~/src/helpers/functions';

export enum EPriceOperationTypes {
    Possess = 'possess',
    Leasing = 'leasing',
    TestDrive = 'test-drive',
    Credit = 'credit',
}

export enum EPriceBuyTypes {
    Sale = 'sale',
    Leasing = 'leasing',
}

export interface IPriceProps {
    id?: number,
    type: EPriceTypes,
    value: number,
    valuePromo?: number,
    duration?: number,
    sort?: number,
    requestId?: number,
}

export interface IPrice extends IPriceProps {
    periodText: string,
    label: string,
    buyType: EPriceBuyTypes,
    getValue: () => number,
}

export interface IPriceOptionProps extends Omit<IPrice, 'duration' | 'value' | 'valuePromo' | 'periodText' | 'buyType' | 'getValue'> {
    value?: IPrice['duration'],
    price: IPrice['value'],
    pricePromo?: IPrice['valuePromo'],
    operType: EPriceOperationTypes,
}

export interface IPriceShopDefault {
    price: number,
    duration: number,
}

export interface IPriceShopCustom {
    date_close_promo: string,
    duration: number,
    lifetime_price: null,
    value: number,
}

export class Price implements IPrice {
    id;
    type;
    value;
    valuePromo;
    duration;
    sort;
    requestId;
    periodText;
    label;
    buyType;
    name: string | null | undefined;

    constructor(props: IPriceProps) {
        this.id = props.id,
        this.type = props.type;
        this.value = props.value;
        this.valuePromo = props.valuePromo;
        this.duration = Number(props.duration);
        this.sort = props.sort;
        this.requestId = props.requestId;

        this.label = this.type === EPriceTypes.Leasing
            ? `Рассрочка (${this.duration} мес.)`
            : 'Покупка';

        this.periodText = this.type === EPriceTypes.Leasing
            ? 'в мес.'
            : '';

        this.buyType = this.type === EPriceTypes.Leasing
            ? EPriceBuyTypes.Leasing
            : EPriceBuyTypes.Sale;
    }

    getValue(): number {
        return this.valuePromo || this.value;
    }
}

export function convertShopPrices(item: any): Price[] {
    if (!item) {
        return [];
    }

    return item.pricesV2 && item.pricesV2.length
        ? convertShopPricesV2(item)
        : convertShopPricesV1(item);
}

function convertShopPricesV1(item: any): Price[] {
    const prices = [];

    const pricesLeasing = new Map<number, IPriceShopDefault>(
        item.prices?.leasing.map((price: IPriceShopDefault) => [price.duration, price]),
    );

    const pricesLeasingPromo = new Map<number, IPriceShopCustom>(
        item.pricesPromo?.leasing?.map((price: IPriceShopCustom) => [price.duration, price]),
    );

    for (const [duration, price] of pricesLeasing) {
        prices.push(new Price({
            type: EPriceTypes.Leasing,
            value: price.price,
            valuePromo: pricesLeasingPromo?.get(duration)?.value,
            duration: price.duration,
        }));
    }

    if (item.prices?.possess) {
        prices.push(new Price({
            type: EPriceTypes.Possess,
            value: item.prices.possess,
            valuePromo: item.pricesPromo?.possess?.value,
        }));
    }

    return prices;
}

function convertShopPricesV2(item: any): Price[] {
    const prices = [];

    const pricesLeasingPromo = new Map<number, IPriceShopCustom>(
        item.pricesPromo?.leasing?.map((price: IPriceShopCustom) => [price.duration, price]),
    );

    item.pricesV2.sort(sortFunction);

    for (const priceElement of item.pricesV2) {
        prices.push(new Price({
            ...priceElement,
            valuePromo:
                priceElement?.promoValue ||
                (priceElement?.duration
                    ? pricesLeasingPromo?.get(priceElement.duration)?.value
                    : item.pricesPromo?.possess?.value),
        }));
    }

    return prices;
}

interface IGetWithConvertedPricesProps {
    prices: IPriceProps[],
}

// Фундоментальная функция, все объекты/массивы объектов нужно прогонять через неё
export function getWithConvertedPrices(
    elementOrArray: Array<IGetWithConvertedPricesProps> | IGetWithConvertedPricesProps,
): Array<IGetWithConvertedPricesProps> | IGetWithConvertedPricesProps {
    if (!elementOrArray) {
        return elementOrArray;
    }

    return Array.isArray(elementOrArray)
        ? elementOrArray.map(element => {
            element.prices = element.prices.map((price: IPriceProps) => new Price(price)).sort(sortFunction);
            return element;
        })
        : (elementOrArray.prices = elementOrArray.prices.map((price: IPriceProps) => new Price(price)).sort(sortFunction)) &&
        elementOrArray;
}
